import { useTranscription } from "@context/TranscriptionContext";
import { useVoice } from "@context/VoiceSessionContext";
import { useCallback } from "react";

/**
 * Voice (`/ws/voice`) is preferred; dictation (`/ws/transcription/stream`) is the fallback while backend `enable_voice_agent` is off.
 * No capability endpoint: the socket answering "voice unavailable" is the probe, remembered for the page load.
 */
type TalkMode = "voice" | "dictation";

type TalkStatus = "idle" | "connecting" | "live" | "stopping" | "error";

/** Names the microphone only when the capturing hook reported a local capture failure. */
function talkErrorTitle(mode: TalkMode, micFailure: boolean): string {
  if (micFailure) return "Microphone error";
  return mode === "voice" ? "Voice session error" : "Recording error";
}

interface TalkToBrian {
  status: TalkStatus;
  error: string | null;
  errorTitle: string;
  speaking: boolean;
  isActive: boolean;
  toggle: () => void;
}

export function useTalkToBrian(): TalkToBrian {
  const dictation = useTranscription();
  const voice = useVoice();

  // Held until reload, deliberately not persisted, so enabling voice takes effect on reload.
  const voiceOff = voice.status === "unavailable";

  const dictationStatus: TalkStatus =
    dictation.status === "recording"
      ? "live"
      : dictation.status === "connecting" || dictation.status === "reconnecting"
        ? "connecting"
        : dictation.status === "stopping"
          ? "stopping"
          : dictation.status === "error"
            ? "error"
            : "idle";

  const voiceStatus: TalkStatus =
    voice.status === "live"
      ? "live"
      : voice.status === "connecting"
        ? "connecting"
        : voice.status === "error"
          ? "error"
          : "idle";

  // Dictation wins a tie: DevInjectPanel can start it with `voiceOff` false, and a leftover voice-probe status must not outrank it.
  const mode: TalkMode =
    dictationStatus !== "idle"
      ? "dictation"
      : voiceStatus !== "idle"
        ? "voice"
        : voiceOff
          ? "dictation"
          : "voice";

  const status: TalkStatus =
    mode === "dictation" ? dictationStatus : voiceStatus;

  const isActive =
    status === "connecting" || status === "live" || status === "stopping";

  const toggle = useCallback(() => {
    if (dictationStatus !== "idle" && dictationStatus !== "error") {
      dictation.stop();
      return;
    }
    if (voice.status === "live" || voice.status === "connecting") {
      voice.stop();
      return;
    }
    if (mode === "dictation") {
      void dictation.start();
      return;
    }
    void voice.start().then((result) => {
      if (result === "unavailable") void dictation.start();
    });
  }, [dictation, dictationStatus, mode, voice]);

  const micFailure = mode === "voice" ? voice.micFailure : dictation.micFailure;

  return {
    status,
    error: mode === "voice" ? voice.error : dictation.error,
    errorTitle: talkErrorTitle(mode, micFailure),
    speaking: voice.speaking,
    isActive,
    toggle,
  };
}
